import type { ChatStatus, ClientChatStatus } from '@/api/models';
import { reportLeak } from '@/lib/telemetry';

/**
 * Status chips, in plain language.
 *
 * The person reading these is not an engineer. They are trusting this platform
 * with their product and they need to know two things at a glance: what is
 * happening, and whether it is their turn. Every chip below answers both.
 *
 * The map is keyed on the full ten-value `ChatStatus` even though a client
 * surface can only ever receive the seven values of `ClientChatStatus`. Two
 * reasons: the three extra values have a defined client projection, so writing
 * them down is cheaper than reasoning about them later, and if one ever does
 * arrive it is projected correctly instead of falling through to an unknown.
 *
 * "changes requested" gets the most attention of any string in this file. It is
 * the one review outcome the client is meant to see, and the version an
 * engineer would write ("changes requested") reads as an accusation or as
 * jargon. What the client actually needs to know is that the work came back to
 * them and they should reply.
 */

export type ChipTone = 'resting' | 'active' | 'attention' | 'fault' | 'held';

export interface StatusCopy {
  /** Two or three words. Goes in the chip. */
  label: string;
  /** One sentence. Goes under the chip in the header and on the project card. */
  detail: string;
  tone: ChipTone;
  /** True when the product is waiting on the person reading this. */
  yourTurn: boolean;
}

const STATUS_COPY: Record<ChatStatus, StatusCopy> = {
  draft: {
    label: 'Ready',
    detail: 'Tell us what you would like changed.',
    tone: 'resting',
    yourTurn: true,
  },
  'agent running': {
    label: 'Working on it',
    detail: 'Your change is being made now. This usually takes a few minutes.',
    tone: 'active',
    yourTurn: false,
  },
  'agent failed': {
    label: 'Stopped early',
    detail: 'The work stopped before it finished. Nothing was published. Try asking again.',
    tone: 'fault',
    yourTurn: true,
  },
  deploying: {
    label: 'Publishing',
    detail: 'The change is being put on your preview site.',
    tone: 'active',
    yourTurn: false,
  },
  'deploy failed': {
    label: 'Preview not updated',
    detail: 'The change was made but the preview site did not update. Ask again and we will retry.',
    tone: 'fault',
    yourTurn: true,
  },
  'pending review': {
    label: 'With your reviewer',
    detail: 'A reviewer is checking the work before it goes live. Nothing is needed from you.',
    tone: 'held',
    yourTurn: false,
  },
  'changes requested': {
    label: 'Back to you',
    detail: 'Your reviewer sent this back with a note. Read it, then reply with what to change.',
    tone: 'attention',
    yourTurn: true,
  },
  // The three below are projected to a client-visible value before they ever
  // reach this surface. They are written out so the projection is explicit.
  'merged to staging': {
    label: 'Ready',
    detail: 'Review is finished. Tell us what you would like changed next.',
    tone: 'resting',
    yourTurn: true,
  },
  'merged to production': {
    label: 'Ready',
    detail: 'Your change is live. Tell us what you would like changed next.',
    tone: 'resting',
    yourTurn: true,
  },
  'promotion failed': {
    label: 'With your reviewer',
    detail: 'A reviewer is checking the work before it goes live. Nothing is needed from you.',
    tone: 'held',
    yourTurn: false,
  },
};

const UNKNOWN_STATUS: StatusCopy = {
  label: 'Checking',
  detail: 'We are confirming where this stands. Refresh in a moment.',
  tone: 'held',
  yourTurn: false,
};

/**
 * Never throws and never renders a raw status string. An unrecognised status is
 * a contract drift, so it reports and shows a neutral holding state - a client
 * seeing `agent running` spelled out on screen would be a white-label failure
 * on its own.
 */
export function statusCopy(status: ClientChatStatus | ChatStatus | string | undefined): StatusCopy {
  if (!status) return UNKNOWN_STATUS;
  const found = STATUS_COPY[status as ChatStatus];
  if (found) return found;
  reportLeak({ kind: 'unknown_status', token: String(status).slice(0, 48) });
  return UNKNOWN_STATUS;
}

export const ALL_STATUS_KEYS = Object.keys(STATUS_COPY) as ChatStatus[];
